import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { FileUpload } from '@/components/ui/file-upload'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { parseError } from '@/lib/errors'
import { EmailDirection, type EmailThread } from '@/models/internalEmail'
import { useSendQaEmail } from '@/models/qaEmail'
import { asFiles, attachmentLabel } from './attachments'

/** The alias the agent told the customer to answer, so the reply resolves. */
function replyTarget(thread: EmailThread): string {
  const outbound = [...thread.messages].reverse().find((m) => m.direction === EmailDirection.OUTBOUND)
  return outbound?.replyTo || outbound?.fromEmail || ''
}

function persona(thread: EmailThread): string {
  return thread.messages.find((m) => m.direction === EmailDirection.INBOUND)?.fromEmail ?? ''
}

/** The agent owes this thread a turn, so silence is expected rather than a bug. */
function awaitingReply(thread: EmailThread): boolean {
  return thread.messages.at(-1)?.direction === EmailDirection.INBOUND
}

export function QaReplyComposer({ thread }: { thread: EmailThread }) {
  const [body, setBody] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const { mutateAsync, isPending } = useSendQaEmail()

  const fromEmail = persona(thread)
  const toEmail = replyTarget(thread)
  const subject = thread.subject.toLowerCase().startsWith('re:') ? thread.subject : `Re: ${thread.subject}`

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!body.trim() || !toEmail) return
    try {
      await mutateAsync({
        fromEmail,
        toEmail,
        subject,
        body,
        attachments,
        threadKey: thread.threadKey,
      })
      setBody('')
      setAttachments([])
      toast.success('Reply ingested')
    } catch (err) {
      toast.error(parseError(err))
    }
  }

  return (
    <form onSubmit={onSubmit} className="bg-card flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex flex-col gap-2">
        <Label htmlFor="qa-reply-body">Reply as the customer</Label>
        <p className="text-muted-foreground text-xs">
          {awaitingReply(thread)
            ? 'Awaiting the agent’s reply…'
            : `${fromEmail} → ${toEmail || 'no agent reply yet'}`}
        </p>
        <Textarea id="qa-reply-body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
      </div>
      <div className="flex items-center gap-3">
        <FileUpload multiple onChange={(files) => setAttachments(asFiles(files))}>
          <Button type="button" variant="outline" size="sm">
            Attach files
          </Button>
        </FileUpload>
        <span className="text-muted-foreground text-xs">{attachmentLabel(attachments)}</span>
      </div>
      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={!body.trim() || !toEmail}
          loading={isPending ? 'Sending…' : undefined}
        >
          Send reply
        </Button>
      </div>
    </form>
  )
}
